<script>
  import { Tooltip } from "paxray-components-svelte";
  import ProcessCard from "./ProcessCard.svelte";
  export let group;
  export let filteredProcesses = [];
  export let visibleProcesses = [];
  export let currentPage = 1;
  export let pageSize = 25;
  export let totalPages = 1;
  export let timeValue;
  export let timeShare = 0;
  export let groupScore = null;
  export let hasActiveFilters = false;
  export let formatDuration;
  export let processCardIcon;
  export let coreKpis;
  export let onProcessClick = () => {};
  export let onPageChange = () => {};
  let expanded = true;
</script>

<section class="process-group" class:empty-group={!filteredProcesses.length} class:collapsed={!expanded} style={`--group-color: ${group.color}`}>
  <div class="group-heading">
    <div>
      <span class="group-marker" aria-label={`${group.type} process group`}>{group.type.slice(0, 1).toUpperCase()}</span>
      <h2>{group.label}</h2>
      <span class="group-time">
        <span>{timeShare}%</span>
        {#if timeShare > 0}
          <span aria-hidden="true">·</span>
          <span>{formatDuration(timeValue)}</span>
        {/if}
      </span>
    </div>
    <div class="group-score">
      <span>Weighted Score <Tooltip text="Weighted performance score for the processes in this group, based on annual active time." placement="right" /></span>
      <strong class:na-value={groupScore === null || groupScore === undefined}>{groupScore ?? "N/A"}</strong>
    </div>
    <div class="group-actions">
      <button
        type="button"
        class="collapse-button"
        aria-label={expanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
        title={expanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
        aria-expanded={expanded}
        on:click={() => (expanded = !expanded)}
      >
        <img src={`/img/assistant/icons/${expanded ? "chevron_up" : "chevron_down_black"}.svg`} alt="" />
      </button>
    </div>
  </div>

  {#if expanded && filteredProcesses.length}
    <div class={`process-cards ${group.type}`}>
      {#each visibleProcesses as process, index}
        <ProcessCard
          {process}
          groupType={group.type}
          groupColor={group.color}
          {index}
          {processCardIcon}
          {coreKpis}
          onProcessClick={onProcessClick}
        />
      {/each}
    </div>
    <div class="pagination">
      <div class="pagination-summary">
        <span>Items per page:</span>
        <strong>{pageSize}</strong>
        <span>
          {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredProcesses.length)}
          of {filteredProcesses.length} items
        </span>
      </div>
      <div>
        <button
          type="button"
          aria-label="Previous page"
          disabled={currentPage === 1}
          on:click={() => onPageChange(currentPage - 1)}
        >‹</button>
        <span>{currentPage} / {totalPages}</span>
        <button
          type="button"
          aria-label="Next page"
          disabled={currentPage === totalPages}
          on:click={() => onPageChange(currentPage + 1)}
        >›</button>
      </div>
    </div>
  {:else if expanded}
    <div class="no-results">
      <span>{hasActiveFilters
        ? "No matching processes."
        : `No ${group.type} processes assigned.`}</span>
    </div>
  {/if}
</section>

<style>
  .process-group { display: flex; min-width: 0; flex-direction: column; overflow: hidden; border: 1px solid #000; border-radius: 0.35rem; background: #fff; }
  .empty-group { display: grid; grid-template-rows: auto minmax(0, 1fr); flex: 1 1 0; min-height: 8rem; }
  .collapsed { flex: 0 0 auto; min-height: 0; }
  .collapsed .group-heading { border-bottom: 0; }
  .group-heading { display: grid; min-height: 2.7rem; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; padding: 0 0.7rem; border-bottom: 1px solid var(--pax-black); }
  .group-heading > div { display: flex; min-width: 0; align-items: center; gap: 0.55rem; }
  .group-marker { display: grid; width: 1.35rem; height: 1.35rem; place-items: center; border-radius: 0.2rem; background: var(--group-color); color: #202329; font-size: 0.68rem; font-weight: 700; }
  .group-heading h2 { margin: 0; color: var(--pax-black); font-size: 0.95rem; font-weight: 700; }
  .group-time { display: inline-flex; align-items: center; gap: 0.55rem; color: var(--pax-black); font-size: 0.95rem; font-weight: 400; white-space: nowrap; }
  .group-time::before { content: "·"; }
  .group-actions { display: flex; grid-column: 3; align-items: center; justify-self: end; gap: 0.45rem; }
  .group-score { grid-column: 2; display: flex; align-items: center; gap: 0.45rem; color: var(--pax-black); }
  .group-score span { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; }
  .group-score strong { font-size: 0.95rem; }
  .collapse-button { display: grid; width: 1.35rem; height: 1.35rem; place-items: center; padding: 0; border: 0; background: transparent; cursor: pointer; }
  .collapse-button img { width: 1rem; height: 1rem; filter: brightness(0); }
  .process-cards { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(10.5rem, 12rem); justify-content: start; gap: 0.65rem; min-height: 8.5rem; overflow-x: auto; padding: 1rem 1.2rem; }
  .process-cards.core { grid-auto-columns: minmax(12rem, 13rem); min-height: 23rem; }
  .pagination { display: flex; min-height: 2.35rem; align-items: center; justify-content: space-between; padding-left: 0.7rem; border-top: 1px solid var(--pax-black); background: #fff; color: var(--pax-black); font-size: 0.7rem; }
  .pagination > div { display: flex; align-items: center; }
  .pagination-summary { height: 100%; gap: 0; }
  .pagination-summary > span { padding: 0 0.55rem; }
  .pagination-summary strong { display: grid; height: 100%; min-width: 2.8rem; place-items: center; border-right: 1px solid #dce3e6; border-left: 1px solid #dce3e6; color: var(--pax-black); font-weight: 500; }
  .pagination button { width: 2.35rem; height: 2.35rem; border: 0; border-left: 1px solid #dce3e6; background: transparent; color: var(--pax-black); font-size: 1.1rem; cursor: pointer; }
  .pagination button:disabled { color: #bcc1c7; cursor: default; }
  .pagination > div:last-child > span { min-width: 4rem; text-align: center; }
  .no-results { display: flex; min-height: 0; align-items: center; justify-content: center; flex-direction: column; gap: 0.25rem; padding: 1rem; color: var(--pax-black); text-align: center; font-size: 0.75rem; }
  .na-value { color: #da1e28; font-weight: 700; text-transform: none; }

  @media (max-width: 78rem) {
    .process-cards { grid-auto-columns: minmax(10rem, 11.5rem); }
    .process-cards.core { grid-auto-columns: minmax(11.5rem, 12.5rem); }
  }

  @media (max-width: 52rem) {
    .process-cards { padding: 0.75rem; }
  }
</style>
